import FavIcon from "@/assets/heart.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { anonymousName, matchAndClubCountryCodes } from "@teamz/common";
import { Lock, UserRound } from "lucide-react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMe } from "@/context/user-context";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { routes } from "@/routes";

type PlayerTeaserType = RouterOutputs["player"]["listPlayersTeaser"][number];

// Carte de la liste joueurs en mode freemium, partagée par la liste teaser et
// les favoris. Deux rendus : profil vitrine (nom et photo en clair) ou profil
// anonymisé (nom d'emprunt flouté). Dans les deux cas la carte ouvre la fiche du
// joueur, servie sans identité pour les profils anonymisés ; seul le contact
// reste verrouillé.
export function PlayerTeaserItem({ player }: { player: PlayerTeaserType }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { user } = useMe();

  const { data: club } = useQuery(trpc.club.getClub.queryOptions({ clubId: user.id }));

  const { mutate } = useMutation(trpc.club.updateFavoritesPlayers.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      queryClient.invalidateQueries(trpc.club.getClubFavoritePlayersTeaser.queryFilter());
    },
  }));

  const isFavorite = club?.favoritesPlayers?.includes(player.id);

  // Un profil vitrine affiche ses classements exacts, les autres la tranche
  // calculée par l'API.
  const utrRanking = player.isShowcase
    ? player.UTRRanking
    : player.UTRBracket && tDyn(t, `common:ranking-bracket.utr.${player.UTRBracket}`);
  const atpRanking = player.isShowcase
    ? player.ATPWTARanking
    : player.ATPBracket && tDyn(t, `common:ranking-bracket.atp.${player.ATPBracket}`);

  return (
    <Link
      to={routes.getPlayerPath(player.userId)}
      state={{ fromPath: routes.players }}
      className="grid grid-cols-1 gap-2 rounded-[18px] border bg-white p-2.5 transition-colors hover:bg-neutral-50 md:grid-cols-2 md:rounded-none md:border-x-0 md:border-t-0 md:first:rounded-t-[18px] md:last:rounded-b-[18px] md:last:border-b-0 lg:grid-cols-[1fr_auto_260px_auto_auto] lg:gap-3"
    >
      <div className="order-1 flex items-center gap-2">
        {player.isShowcase
          ? (
              <img
                src={player.imgUrl ?? undefined}
                alt=""
                className="pointer-events-none size-14 shrink-0 select-none rounded-xl object-cover"
              />
            )
          : (
              <div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-neutral-400">
                <UserRound className="size-6" />
              </div>
            )}
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            {player.isShowcase
              ? <h1 className="line-clamp-1 text-ellipsis text-lg font-semibold">{player.fullName}</h1>
              : (
                  <>
                    {/* Nom d'emprunt de longueur variable, masqué par un flou purement
                        visuel : les lecteurs d'écran lisent le libellé « profil
                        verrouillé » ci-dessous. */}
                    <h1 aria-hidden className="select-none truncate text-lg font-semibold text-neutral-450 blur-[6px]">
                      {anonymousName(player.id)}
                    </h1>
                    <span className="sr-only">{t("common:freemium.locked-player")}</span>
                    <Lock className="size-4 shrink-0 text-neutral-400" />
                  </>
                )}
          </div>
          <div className="flex items-center gap-2">
            {player.gender === "male"
              ? (
                  <Badge variant="blue-dark" size="compact">
                    H
                  </Badge>
                )
              : (
                  <Badge variant="purple-dark" size="compact">
                    F
                  </Badge>
                )}

            <span className="text-nowrap text-sm font-medium text-neutral-500">
              {t("profile:years", { age: player.age })}
            </span>

            <div className="flex w-auto max-w-48 shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 p-0.5 lg:hidden">
              <CircleFlag countryCode={player.nationalityCode.toLowerCase()} loading="lazy" className="size-4 rounded-full" />
              <span className="pr-2 text-sm font-medium">
                {player.nationalityCode}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="order-2 hidden items-center justify-center lg:flex">
        <div className="flex h-fit w-auto max-w-48 shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 p-0.5">
          <CircleFlag countryCode={player.nationalityCode.toLowerCase()} loading="lazy" className="size-4 rounded-full" />
          <span className="pr-2 text-sm font-medium">
            {player.nationalityCode}
          </span>
        </div>
      </div>

      {/* FFT en valeur réelle comme dans la liste complète ; UTR et ATP en
          tranches, regroupées par l'API — sauf sur un profil vitrine, servi
          avec ses classements exacts. */}
      <div className="order-3 flex divide-x divide-neutral-400 rounded-lg border lg:w-[260px]">
        {player.FFTRanking
          ? (
              <div className="flex min-w-0 flex-1 basis-0 flex-col-reverse items-center justify-center gap-0 p-3 lg:px-1.5 lg:py-1">
                <span className="text-sm font-semibold text-neutral-500 lg:text-xs">FFT</span>
                <span className="text-nowrap text-sm font-bold">
                  {/* Un T100 s'affiche « N » suivi de son numéro, comme dans la
                      liste complète. Le numéro peut manquer en base, d'où le
                      repli sur une chaîne vide plutôt qu'un « null » affiché. */}
                  {player.FFTRanking === "T100" ? `N${player.FFTRankingNumber ?? ""}` : player.FFTRanking}
                </span>
              </div>
            )
          : (
              <div className="flex min-w-0 flex-1 basis-0 items-center justify-between gap-2 rounded-l-lg bg-neutral-50 p-3 text-neutral-450 lg:justify-center lg:px-1.5">
                <span className="text-sm font-semibold lg:hidden">FFT</span>
                <DotsIcon />
              </div>
            )}
        {utrRanking
          ? (
              <div className="flex min-w-0 flex-1 basis-0 flex-col-reverse items-center justify-center gap-0 p-3 lg:px-1.5 lg:py-1">
                <span className="text-sm font-semibold text-neutral-500 lg:text-xs">UTR</span>
                <span className="text-nowrap text-sm font-bold">{utrRanking}</span>
              </div>
            )
          : (
              <div className="flex min-w-0 flex-1 basis-0 items-center justify-between gap-2 bg-neutral-50 p-3 text-neutral-450 lg:justify-center lg:px-1.5">
                <span className="text-sm font-semibold lg:hidden">UTR</span>
                <DotsIcon />
              </div>
            )}
        {atpRanking
          ? (
              <div className="flex min-w-0 flex-1 basis-0 flex-col-reverse items-center justify-center gap-0 p-3 lg:px-1.5 lg:py-1">
                <span className="text-sm font-semibold text-neutral-500 lg:text-xs">{player.gender === "female" ? "WTA" : "ATP"}</span>
                <span className="text-nowrap text-sm font-bold">{atpRanking}</span>
              </div>
            )
          : (
              <div className="flex min-w-0 flex-1 basis-0 items-center justify-between gap-2 rounded-r-lg bg-neutral-50 p-3 text-neutral-450 lg:justify-center lg:px-1.5">
                <span className="text-sm font-semibold lg:hidden">{player.gender === "female" ? "WTA" : "ATP"}</span>
                <DotsIcon />
              </div>
            )}
      </div>

      {/* Disponibilités par pays : mêmes pastilles que la liste complète,
          les pays non couverts restent en filigrane. */}
      <div className="order-4 flex shrink-0 items-center justify-between gap-2 rounded-xl border px-2 py-1.5 lg:order-4 lg:w-fit lg:justify-center">
        <span className="shrink-0 text-sm font-medium text-neutral-700 lg:hidden">
          {t("profile:season", { season: new Date().getFullYear() })}
        </span>
        <div className="flex items-center gap-2">
          {matchAndClubCountryCodes.map(code => (
            <CircleFlag
              key={code}
              countryCode={code}
              loading="lazy"
              className={cn(
                "pointer-events-none block size-6 rounded-full border-2 border-white opacity-15 shadow-md",
                player.matchAvailabilitiesCountries?.includes(code) && "opacity-100",
              )}
            />
          ))}
        </div>
      </div>

      {/* Le club peut constituer sa liste avant de s'abonner : le bouton vit
          dans la carte, qui est un lien, d'où la neutralisation du clic. */}
      <div className="order-5 flex items-center lg:justify-center">
        <Button
          variant="outline"
          size="compact"
          aria-pressed={isFavorite}
          aria-label={t("common:mobile-nav.favorites")}
          className="w-full active:border-neutral-300 lg:w-fit"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            mutate({ playerId: player.id });
          }}
        >
          <FavIcon className={isFavorite ? "fill-[#FE5000]" : "fill-white"} />
          <span className="lg:hidden">{t("common:mobile-nav.favorites")}</span>
        </Button>
      </div>
    </Link>
  );
}
